import React, { useState } from 'react';
import { Service, services } from '../data/services';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

const ServiceCard: React.FC<{ service: Service }> = ({ service }) => {
  const handleBookingConfirm = () => {
    window.open(service.bookingLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:scale-[1.02] flex flex-col h-full">
      <div className="h-48 w-full overflow-hidden">
        <img 
          src={service.image} 
          alt={`${service.name} - ${service.description}`}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
        />
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">{service.name}</h3>
          <p className="text-gray-700 dark:text-gray-300 text-lg">{service.price}</p>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-6 flex-grow">{service.description}</p>
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 dark:text-gray-900 font-medium py-2 rounded-full transition-all duration-300 mt-auto">
              Book now
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Booking Confirmation</AlertDialogTitle>
              <AlertDialogDescription>
                Please remember the style you have selected and apply in the booking confirmation form.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleBookingConfirm}>
                Ok, Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

const ServicesSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'men' | 'women' | 'kids'>('men');

  const filteredServices = services.filter(service => service.category === activeTab);

  return (
    <section id="services" className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">Our Services</h2>
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-6"></div>
          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto font-['Instrument_Serif']">
          Book now for expert cuts and styles tailored to your look.
          </p>
        </div>
        
        {/* Category Tabs */}
        <div className="flex justify-center mb-12">
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-full p-1 flex space-x-1">
            {['men', 'women', 'kids'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as 'men' | 'women' | 'kids')}
                className={`px-8 py-2 rounded-full transition-all duration-300 ${
                  activeTab === tab
                    ? 'bg-amber-500 text-gray-900 dark:text-gray-900'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredServices.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;